import React, { useEffect, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import useWakeLock from '../hooks/useWakeLock';
import './Viewer.scss';
import {
  ConnectionStatusEnum,
  ContentStatusEnum,
  PageDetails,
  SessionBackgroundData
} from './Session';
import Content from './Content';
import PageMenu from './PageMenu';
import Background from '../components/Background';
import ContentNavigation from '../components/ContentNavigation';
import { FaExpand } from 'react-icons/fa';
import PreviewBanner from '../components/PreviewBanner';

export interface ViewerProps {
  pages: PageDetails[];
  allPages: PageDetails[];
  blank: boolean;
  allowUserNavigation: boolean;
  allowViewAllPages: boolean;
  connectionStatus: ConnectionStatusEnum;
  startAtLatest: boolean;
  contentStatus: ContentStatusEnum;
  isPreview: boolean;
  isReviewingAfterEnded: boolean;
  background: SessionBackgroundData | null;
  viewEnd: () => void;
}

const Viewer: React.FC<ViewerProps> = ({
  pages,
  allPages,
  blank,
  allowUserNavigation,
  allowViewAllPages,
  connectionStatus,
  startAtLatest,
  contentStatus,
  isPreview,
  isReviewingAfterEnded,
  background,
  viewEnd
}) => {
  const [showPage, setShowPage] = useState<number | null>(
    startAtLatest ? null : 0
  );
  const [allPagesIndex, setAllPagesIndex] = useState<number | null>(null);

  useEffect(() => {
    if (allPagesIndex !== null) {
      const allPagesPage = allPages.at(allPagesIndex);
      if (allPagesPage !== null) {
        const currentPage = pages.findLastIndex(
          (p) => p.reference === allPagesPage?.reference
        );
        if (currentPage !== -1) {
          setShowPage(currentPage);
        }
      }
    }
  }, [allPagesIndex]);

  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  useEffect(() => {
    requestWakeLock();

    return () => {
      releaseWakeLock();
    };
  }, []);

  const handle = useFullScreenHandle();

  const toggleFullScreen = () => {
    if (!document.fullscreenEnabled) {
      return;
    }
    if (handle.active) {
      handle.exit();
    } else {
      handle.enter();
    }
  };

  const first = () => {
    if (pages.length > 0) {
      setShowPage(0);
      setAllPagesIndex(null);
    }
  };
  const pause = () => {
    if (showPage === null && pages.length > 0) {
      setShowPage(pages.length - 1);
      setAllPagesIndex(null);
    }
  };
  const resume = () => {
    setShowPage(null);
    setAllPagesIndex(null);
  };
  const next = () => {
    if (showPage !== null && showPage < pages.length - 1) {
      if (showPage === pages.length) {
        // Reset to live
        setShowPage(null);
      } else {
        // Set next
        setShowPage(showPage + 1);
      }
    }
    setAllPagesIndex(null);
  };
  const previous = () => {
    if (showPage === null) {
      setShowPage(pages.length - 2);
    } else if (showPage > 0) {
      setShowPage(showPage - 1);
    }
    setAllPagesIndex(null);
  };

  const currentPageNumber = () => {
    if (showPage === null) {
      return -1;
    }
    return showPage;
  };

  const showBlank = () => {
    if (!blank) {
      // If there's no blank command, show the content
      return false;
    }
    if (showPage === null && allPagesIndex === null) {
      // If the user is watching live, show blank
      return true;
    }
    return false;
  };

  const contentWindow = () => {
    if (allPagesIndex !== null) {
      return (
        <Content
          page={allPages.at(allPagesIndex)}
          contentStatus={
            showPage === null ? contentStatus : ContentStatusEnum.Showing
          }
        />
      );
    }
    return (
      <Content
        page={getCurrentPage()}
        contentStatus={
          showPage === null ? contentStatus : ContentStatusEnum.Showing
        }
      />
    );
  };

  const getConnectionStatus = () => {
    if (connectionStatus === ConnectionStatusEnum.Connected) {
      return { className: 'connected', text: 'Connected' };
    }
    if (connectionStatus === ConnectionStatusEnum.Connecting) {
      return { className: 'connecting', text: 'Connecting...' };
    }
    if (connectionStatus === ConnectionStatusEnum.Reconnecting) {
      return { className: 'reconnecting', text: 'Reconnecting...' };
    }
    return { className: 'disconnected', text: 'Disconnected' };
  };

  const getConnectionStatusLabel = () => {
    const data = getConnectionStatus();
    if (data.className === 'connected') {
      return null;
    }
    return (
      <div
        className={`status ${data.className}`}
        role="status"
        aria-live="polite"
      >
        <div className="status-indicator"></div>
        <span className="status-text">{data.text}</span>
      </div>
    );
  };

  const getCurrentPage = () => {
    if (showBlank()) {
      return undefined;
    }
    return pages.at(currentPageNumber());
  };

  const getCurrentPageIndex = () => {
    if (showBlank()) {
      return undefined;
    }
    return currentPageNumber();
  };

  const getAllPagesIndex = () => {
    if (!allowViewAllPages) {
      return null;
    }

    if (allPagesIndex !== null) {
      return allPagesIndex;
    }

    const currentPage = getCurrentPage();
    return allPages.findIndex((p) => p.reference === currentPage?.reference);
  };

  return (
    <FullScreen handle={handle}>
      <Background background={background} />
      <div className="content-container">
        <PreviewBanner isPreview={isPreview} />
        <div className="header-navigation">
          {allowViewAllPages && (
            <PageMenu
              pages={allPages}
              selectedPageIndex={getAllPagesIndex()}
              selectPage={(pageIndex) => setAllPagesIndex(pageIndex)}
              autoSelectLatest={false}
            />
          )}
          {!allowViewAllPages && (
            <PageMenu
              pages={pages}
              selectedPageIndex={getCurrentPageIndex()}
              selectPage={(pageIndex) => setShowPage(pageIndex)}
              autoSelectLatest={true}
            />
          )}
          {document.fullscreenEnabled && (
            <button
              className="icon-button fullscreen-icon"
              title="Full Screen"
              onClick={toggleFullScreen}
            >
              <FaExpand />
            </button>
          )}
        </div>
        {contentWindow()}
        <div className="content-footer">
          <div
            className="icon-button footer-connection-status-icon"
            title="Connection Status"
          >
            {getConnectionStatusLabel()}
          </div>
          <ContentNavigation
            allowUserNavigation={allowUserNavigation}
            first={first}
            pause={pause}
            resume={resume}
            next={next}
            previous={previous}
            isPaused={showPage !== null}
            isReviewingAfterEnded={isReviewingAfterEnded}
            viewEnd={viewEnd}
          ></ContentNavigation>
        </div>
      </div>
    </FullScreen>
  );
};

export default Viewer;
